import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { DocumentsTableHeader } from 'components/DocumentsTableHeader';
import { DocumentsTableRow } from 'components/DocumentsTableRow';
import { SummaryRow } from 'components/SummaryRow';

import styles from 'css_modules/DocumentsTable.module.css';
import { ProgressSpinner } from "./ProgressSpinner";

export const DocumentsTable = ({
    checkedDocs,
    docs,
    focusedDocId,
    mainChecked,
    setCheckedDocs,
    setMainChecked,
    setOrdering,
    visibleState,
    user,
    fields,
    visibleFields,
    loading,
    ordering,
    docsCustomTypes,
    isSnappedRight,
    ...tableRowProps
}) => {
    const location = useLocation();
    const containerRef = useRef(null);
    const prevWidthsBeforeSnap = useRef(null);
    const isFirstRender = useRef(true);

    const isTemplates = location.pathname === '/my_templates';

    useEffect(() => {
        const lastReloaded = localStorage.getItem('lastReloaded');
        const now = new Date().getTime();

        if (!lastReloaded) {
            window.location.reload(true);
            localStorage.setItem('lastReloaded', now.toString());
        } else {
            const diff = now - parseInt(lastReloaded);
            const oneHour = 3600000;

            if (diff > oneHour) {
                window.location.reload(true);
                localStorage.setItem('lastReloaded', now.toString());
            }
        }
    }, []);

    const visibleCheckbox = true;

    const [columnWidths, setColumnWidths] = useState(() => {
        const stored = localStorage.getItem('documentTableWidths' + user.pk + location.pathname);

        if (stored) return JSON.parse(stored);

        return visibleFields.map((item) => {
            const keys = item.split(/\.(.*)/);
            return fields[keys[0]].items[keys[1]].width || '200px';
        });
    });

    // Ref to hold the latest columnWidths for hooks without causing extra re-subscribes
    const columnWidthsRef = useRef(columnWidths);
    columnWidthsRef.current = columnWidths;

    useEffect(() => {
        localStorage.setItem(
            'documentTableWidths' + user.pk + location.pathname,
            JSON.stringify(columnWidths)
        );
    }, [columnWidths, user.pk, location.pathname]);

    const getTableClassName = () => {
        if (!visibleCheckbox) return styles.tableWithoutCheckbox;
        if (!visibleState) return styles.tableWithoutState;
        return styles.tableFull;
    };

    const getAutoFitWidth = (colIndex) => {
        const rows = document.querySelectorAll('tbody tr');

        let maxWidth = 60;

        rows.forEach((row) => {
            const cell = row.children[(visibleCheckbox ? 1 : 0) + colIndex];

            if (!cell) return;
            
            const text = cell.querySelector('span');
            
            if (!text) return;
            
            maxWidth = Math.max(maxWidth, text.scrollWidth);
        });

        return maxWidth + 24;
    };

    const autoFitColumn = (colIndex) => {
        const newWidths = [...columnWidths];
        let nw = getAutoFitWidth(colIndex);
        newWidths[colIndex] = `${nw}px`;
        
        setColumnWidths(newWidths);
        return nw;
    };

    const stretchColumnsToFit = () => {
        if (!containerRef.current) return;

        const totalWidth = containerRef.current.offsetWidth;

        const checkboxWidth = visibleCheckbox ? 28 : 0;
        const fillerWidth = 80;

        const available = totalWidth - checkboxWidth - fillerWidth;

        const minWidths = visibleFields.map((_, colIndex) => {
            const headerCells = document.querySelectorAll('th');

            const headerEl =
                headerCells[colIndex]?.querySelector?.(`.${styles.titleBlock}`);

            const headerMin = headerEl
                ? Math.ceil(headerEl.scrollWidth) + 20
                : 120;

            return headerMin;
        });

        const minTotal = minWidths.reduce((a, b) => a + b, 0);

        if (minTotal >= available) {
            setColumnWidths(minWidths.map(w => `${w}px`));
            return;
        }

        const extra = available - minTotal;

        const scaled = minWidths.map(w => {
            const share = w / minTotal;
            return Math.floor(w + extra * share);
        });

        setColumnWidths(scaled.map(w => `${w}px`));
    };

    const stretchRef = useRef(stretchColumnsToFit);
    stretchRef.current = stretchColumnsToFit;

    // Automatically stretch columns whenever on the templates page
    useEffect(() => {
        if (!isTemplates) return;

        let animationFrameId;
        const observer = new ResizeObserver(() => {
            animationFrameId = requestAnimationFrame(() => {
                if (stretchRef.current) {
                    stretchRef.current();
                }
            });
        });

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        if (stretchRef.current) {
            stretchRef.current();
        }

        return () => {
            observer.disconnect();
            if (animationFrameId) cancelAnimationFrame(animationFrameId);
        };
    }, [isTemplates, visibleFields]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (isSnappedRight) {
            if (!prevWidthsBeforeSnap.current) {
                prevWidthsBeforeSnap.current = columnWidthsRef.current;
            }

            let animationFrameId;
            const observer = new ResizeObserver(() => {
                animationFrameId = requestAnimationFrame(() => {
                    if (stretchRef.current) {
                        stretchRef.current();
                    }
                });
            });

            if (containerRef.current) {
                observer.observe(containerRef.current);
            }

            return () => {
                observer.disconnect();
                if (animationFrameId) cancelAnimationFrame(animationFrameId);
            };
        } else if (prevWidthsBeforeSnap.current) {
            setColumnWidths(prevWidthsBeforeSnap.current);
            prevWidthsBeforeSnap.current = null;
        }
    }, [isSnappedRight]);

    if (visibleFields.length !== columnWidths.length) {
        stretchColumnsToFit();
    }

    const isStavColumn = (item) => {
        const keys = item.split(/\.(.*)/);
        return fields[keys[0]].items[keys[1]].name === 'Stav';
    };

    const handleColumnResize = (index, requestedWidth) => {
        if (!isTemplates) {
            const headerCells = document.querySelectorAll('th');
            const headerEl = headerCells[index]?.querySelector?.(`.${styles.titleBlock}`);
            const headerMin = headerEl ? Math.ceil(headerEl.scrollWidth) + 20 : 120;
            const isStav = isStavColumn(visibleFields[index]);
            const stavMin = isStav ? getAutoFitWidth(index) : 0;
            const minWidth = Math.max(headerMin, stavMin);

            const newWidth = Math.max(minWidth, requestedWidth);
            const newWidths = [...columnWidths];
            newWidths[index] = `${newWidth}px`;
            setColumnWidths(newWidths);
            return;
        }

        const numericWidths = columnWidths.map(w => parseFloat(w) || 200);
        const headerCells = document.querySelectorAll('th');

        const minWidths = visibleFields.map((item, colIdx) => {
            const headerEl = headerCells[colIdx]?.querySelector?.(`.${styles.titleBlock}`);
            const headerMin = headerEl ? Math.ceil(headerEl.scrollWidth) + 20 : 120;
            const isStav = isStavColumn(item);
            const stavMin = isStav ? getAutoFitWidth(colIdx) : 0;
            return Math.max(headerMin, stavMin);
        });

        const currentWidth = numericWidths[index];
        const targetWidth = Math.max(minWidths[index], requestedWidth);
        const delta = targetWidth - currentWidth;

        if (Math.abs(delta) < 0.1) return;

        const newWidths = [...numericWidths];

        if (delta > 0) {
            let totalSlack = 0;
            const slacks = visibleFields.map((_, colIdx) => {
                if (colIdx === index) return 0;
                const slack = Math.max(0, numericWidths[colIdx] - minWidths[colIdx]);
                totalSlack += slack;
                return slack;
            });

            if (totalSlack <= 0) return;

            const actualDelta = Math.min(delta, totalSlack);
            newWidths[index] = currentWidth + actualDelta;

            visibleFields.forEach((_, colIdx) => {
                if (colIdx !== index && slacks[colIdx] > 0) {
                    newWidths[colIdx] = numericWidths[colIdx] - (slacks[colIdx] / totalSlack) * actualDelta;
                }
            });
        } else {
            const actualDelta = Math.max(delta, minWidths[index] - currentWidth);
            const extraSpace = -actualDelta;

            if (extraSpace <= 0) return;

            newWidths[index] = currentWidth + actualDelta;

            let otherTotal = 0;
            visibleFields.forEach((_, colIdx) => {
                if (colIdx !== index) {
                    otherTotal += numericWidths[colIdx];
                }
            });

            if (otherTotal > 0) {
                visibleFields.forEach((_, colIdx) => {
                    if (colIdx !== index) {
                        newWidths[colIdx] = numericWidths[colIdx] + extraSpace * (numericWidths[colIdx] / otherTotal);
                    }
                });
            }
        }

        const targetSum = numericWidths.reduce((a, b) => a + b, 0);
        let roundedWidths = newWidths.map(w => Math.round(w));
        const roundedSum = roundedWidths.reduce((a, b) => a + b, 0);
        const diff = targetSum - roundedSum;

        if (diff !== 0) {
            const adjIndex = visibleFields.findIndex((_, i) => i !== index);
            if (adjIndex !== -1) {
                roundedWidths[adjIndex] += diff;
            }
        }

        setColumnWidths(roundedWidths.map(w => `${w}px`));
    };

    const ormFormat = (str) => {
        let ormFormatedStr = str.replace("document_info", 'info').replace('.', '__');
        if (str === 'note') {
            ormFormatedStr = 'info__note';
        }
        return ormFormatedStr;
    };

    const drawHeader = () => {
        return visibleFields.map((item, index) => {
            const keys = item.split(/\.(.*)/);
            
            let selected = 0;

            if (ordering.endsWith(ormFormat(keys[1])) && !ordering.startsWith('-')) {
                selected = -1;
            }
            if (ordering.endsWith(ormFormat(keys[1])) && ordering.startsWith('-')) {
                selected = 1;
            }

            const isStav = isStavColumn(item);
            const minStavWidth = isStav ? getAutoFitWidth(index) : null;

            return (
                <DocumentsTableHeader
                    key={'h' + keys[1]}
                    index={index}
                    columnWidths={columnWidths}
                    setColumnWidths={setColumnWidths}
                    onColumnResize={handleColumnResize}
                    autoFitColumn={autoFitColumn}
                    setOrdering={setOrdering}
                    ordering={ordering}
                    orderBy={ormFormat(keys[1])}
                    selected={selected}
                    forceMinWidth={minStavWidth}
                >
                    {fields[keys[0]].items[keys[1]].name}
                </DocumentsTableHeader>
            );
        });
    };

    const styleColumns = () => ({
        gridTemplateColumns:
            (visibleCheckbox ? '1.75rem ' : '') +
            columnWidths.join(' ') +
            ' 3.5rem'
    });

    if (loading) return <ProgressSpinner />;

    return (
        <>
            <div className={styles.content} ref={containerRef}>
                <div
                    style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 21,
                        background: "white"
                    }}
                >
                    <div style={{ display: "flex", width: "100%" }}>
                        <table
                            className={getTableClassName()}
                            style={{
                                ...styleColumns(),
                                tableLayout: "fixed"
                            }}
                        >
                            <tbody>
                                <tr>
                                    {visibleCheckbox && (
                                        <td className={styles.checkboxCell}>
                                            <input
                                                type="checkbox"
                                                checked={mainChecked}
                                                onChange={(e) => {
                                                    setMainChecked(e.target.checked);
                                                    const activeDocs =
                                                        docs.filter(doc => (location.pathname === '/documents' || doc.is_active || location.pathname === '/archive' || location.pathname === '/my_templates') && !doc.awaiting_capture);
                                                    setCheckedDocs(
                                                        e.target.checked
                                                            ? activeDocs.map(d => d.uuid)
                                                            : []
                                                    );
                                                }}
                                            />
                                        </td>
                                    )}

                                    {drawHeader()}

                                    <div
                                        style={{
                                            width: "3.5rem",
                                            position: "sticky",
                                            right: 0,
                                            zIndex: 30,
                                            marginLeft: "-0.1rem",
                                            backgroundColor: "var(--light)",
                                            borderBottom: "1px solid var(--semilight)",
                                            borderLeft: "2px solid var(--semilight)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}
                                    >
                                        {!isTemplates && (
                                            <button
                                                onClick={stretchColumnsToFit}
                                                style={{
                                                    background: "transparent",
                                                    border: "none",
                                                    padding: 0,
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: "100%",
                                                    height: "100%"
                                                }}
                                            >
                                                <svg
                                                    width="20"
                                                    height="20"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M9 6L4 12L9 18"
                                                        stroke="var(--purple)"
                                                        strokeWidth="3"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                    <path
                                                        d="M15 6L20 12L15 18"
                                                        stroke="var(--purple)"
                                                        strokeWidth="3"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <table
                    className={styles.tableBodyScrollable}
                    style={styleColumns()}
                >
                    <tbody>
                        {docs.map((doc, index) => (
                            <DocumentsTableRow
                                key={doc.uuid}
                                doc={doc}
                                index={index / docs.length}
                                docsCustomTypes={docsCustomTypes}
                                focused={focusedDocId === doc.uuid}
                                visibleCheckbox={visibleCheckbox}
                                visibleState={visibleState}
                                checkedDocs={checkedDocs}
                                setCheckedDocs={setCheckedDocs}
                                setMainChecked={setMainChecked}
                                user={user}
                                fields={fields}
                                visibleFields={visibleFields}
                                {...tableRowProps}
                            />
                        ))}
                    </tbody>
                </table>

                <div className={styles.stretch} />
            </div>

            <table className={styles.summaryRow} style={styleColumns()}>
                <tbody>
                    <SummaryRow
                        docs={docs}
                        visibleFields={visibleFields}
                        visibleCheckbox={visibleCheckbox}
                    />
                </tbody>
            </table>
        </>
    );
};